import { useEffect, useRef } from 'react';
import { Box, Drawer, Stack } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  kpiPanelOpen,
  selectedWorkpackIdForKpiTracker,
  selectedLogTemplateType,
  selectedCostLogsTemplateForKpi,
  qaqcPanelOpen,
  qaqcPanelOpenIntent,
  kpiPanelStructureIdOverride,
  kpiPanelSnackbarMessage,
  kpiPanelWbsFilterSnapshot,
  selectedWbsFilter,
} from '../../state';
import { LogTemplateType } from '@/__generated__/graphql';
import { KpiPanel } from './KpiPanel';
import { QaQcPanel } from '../../QaQcPanel';
import { IndirectPobLogDialog } from './IndirectPobLogDialog';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useKpiWorkpackUrlSync } from '@/components/Analysis/Insights/deeplink';
import { Snackbar } from '@/components/shared/Snackbar';

const DRAWER_WIDTH = '60vw';

export const KpiPanelDrawer = () => {
  const [isOpen, setIsOpen] = useAtom(kpiPanelOpen);
  const [workpackId, setWorkpackId] = useAtom(selectedWorkpackIdForKpiTracker);
  const isQaqcOpen = useAtomValue(qaqcPanelOpen);
  const setQaqcPanelOpen = useSetAtom(qaqcPanelOpen);
  const setQaqcPanelOpenIntent = useSetAtom(qaqcPanelOpenIntent);
  const navbarHeight = useNavbarHeight();

  // Reset hooks for template state
  const setSelectedCostLogsTemplateForKpi = useSetAtom(selectedCostLogsTemplateForKpi);
  const setSelectedLogTemplateType = useSetAtom(selectedLogTemplateType);
  const setKpiPanelStructureIdOverride = useSetAtom(kpiPanelStructureIdOverride);
  const [wbsFilterSnapshot, setWbsFilterSnapshot] = useAtom(kpiPanelWbsFilterSnapshot);
  const setSelectedWbsFilter = useSetAtom(selectedWbsFilter);
  const [snackbar, setSnackbar] = useAtom(kpiPanelSnackbarMessage);

  useKpiWorkpackUrlSync(workpackId, setWorkpackId, isOpen, setIsOpen);

  // Reset snackbar state when the drawer unmounts so a message triggered on one page doesn't
  // re-surface when another mounted drawer reads the same atom on the next page.
  useEffect(() => {
    return () => {
      setSnackbar({ shouldShow: false, content: undefined });
    };
  }, [setSnackbar]);

  // Run panel close cleanup whenever the panel transitions from open to closed, so the cleanup
  // is triggered regardless of the close path (header X-button, backdrop click, escape key).
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setQaqcPanelOpen(false);
      setQaqcPanelOpenIntent(undefined);
      setSelectedLogTemplateType(LogTemplateType.WorklogTemplate);
      setKpiPanelStructureIdOverride(undefined);
      if (wbsFilterSnapshot) {
        setSelectedWbsFilter(wbsFilterSnapshot.value);
        setWbsFilterSnapshot(undefined);
      }
    }
    wasOpenRef.current = isOpen;
  }, [
    isOpen,
    setQaqcPanelOpen,
    setQaqcPanelOpenIntent,
    setSelectedLogTemplateType,
    setKpiPanelStructureIdOverride,
    wbsFilterSnapshot,
    setSelectedWbsFilter,
    setWbsFilterSnapshot,
  ]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSnackbarClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbar({ shouldShow: false, content: undefined });
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        PaperProps={{ sx: { width: DRAWER_WIDTH, backgroundColor: 'background.paper' } }}
        transitionDuration={300}
      >
        <Stack
          id="kpi-panel"
          sx={{
            paddingTop: `${navbarHeight}px`,
            height: '100%',
            position: 'relative',
            minHeight: 0,
          }}
        >
          <Stack sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <KpiPanel />
          </Stack>
          {isQaqcOpen ? (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                top: `${navbarHeight}px`,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.paper',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <QaQcPanel
                  onClose={() => {
                    setQaqcPanelOpen(false);
                    setQaqcPanelOpenIntent(undefined);
                  }}
                />
              </Box>
            </Box>
          ) : null}
        </Stack>
      </Drawer>
      <Snackbar open={snackbar.shouldShow} onClose={handleSnackbarClose}>
        {snackbar.content}
      </Snackbar>
      <IndirectPobLogDialog />
    </>
  );
};
